import packagesSearch from '../../../utils/packages-search'
import { formatDate } from '../../../utils/date-helper'
import { FLIGHT, CAR } from '../../Packages/SearchForm/TripTypes'
import { HOTEL_FORM_STATE_TYPE, HOTEL_FORM_SUBMIT_TYPE } from '../types'
import { STAY, PromoDeal } from '../constants'
import analytics from '../../../utils/analytics'
import {
  HOTEL_SEARCH_DATA,
  persistSearchData
} from '../../../utils/local-forage-helper'
import {
  ANALYTICS_ACTION_HOTELS_SEARCH,
  ANALYTICS_CATEGORY_HOME,
  ANALYTICS_CATEGORY_HOTELS
} from '../../../constants/analytics'

export const initialState: HOTEL_FORM_STATE_TYPE = {
  endLocation: null,
  startLocation: null,
  startDate: '',
  endDate: '',
  roomCount: 1,
  adultCount: 2,
  childrenCount: 0,
  childrenAges: [],
  tripType: STAY,
  gaCategory: ANALYTICS_CATEGORY_HOTELS,
  gaAction: ANALYTICS_ACTION_HOTELS_SEARCH
}

export const fields = [
  'startLocation',
  'endLocation',
  'startDate',
  'endDate',
  'roomCount',
  'adultCount',
  'childrenCount',
  'childrenAges',
  'tripType',
  'gaCategory'
]

export const getHotelMultiOccParam = ({
  adultCount,
  childrenAges
}: {
  adultCount: number
  childrenAges: number[]
}) => {
  const childrenCount = childrenAges.length
  const childrenAgesParam = childrenCount > 0 ? childrenAges.join(',') : ''
  return `/adults/${adultCount}${
    childrenCount > 0 ? `/children/${childrenAgesParam}` : ''
  }`
}

const baseURL = 'https://findgroupdeals.hotelplanner.com/Priceline.cfm?City='

export const groupDealsURL = ({
  location = '',
  startDate,
  endDate
}: {
  location: string
  startDate: string
  endDate: string
}) => {
  return `${baseURL}${encodeURIComponent(
    location
  )}&CheckIn=${encodeURIComponent(
    formatDate(startDate)
  )}&CheckOut=${encodeURIComponent(formatDate(endDate))}`
}

export const formatHotelsURL = ({
  endLocation,
  startDate,
  endDate,
  roomCount,
  adultCount,
  childrenAges,
  dealType,
  SEMSearch,
  SEMSEARCH
}: HOTEL_FORM_SUBMIT_TYPE) => {
  if (endLocation === null) {
    return
  }
  const { type: searchType } = endLocation
  const updatedSearchType = searchType === 'HOTEL' ? 'CITY' : searchType
  const location =
    searchType === 'HOTEL'
      ? endLocation.cityID
      : endLocation.id || encodeURIComponent(endLocation.itemName)
  const dealTypeUrlMap = {
    [PromoDeal.EXPRESS_DEAL]: '/sopq',
    [PromoDeal.COLLECTIONS]: '/collections',
    [PromoDeal.NHA]: '/nha'
  }
  const dealTypeParam =
    dealType === 'EXPRESS_DEAL' ||
    dealType === 'COLLECTIONS' ||
    dealType === 'NHA'
      ? dealTypeUrlMap[dealType]
      : ''
  const multiOcc = getHotelMultiOccParam({
    adultCount,
    childrenAges
  })
  const queries = []
  if (searchType === 'HOTEL' && !dealTypeParam) {
    queries.push(`preferredhotelids=${endLocation.id}`)
  }
  if (SEMSearch) {
    queries.push(`SEMSearch=${SEMSearch}`)
  }
  if (SEMSEARCH) {
    queries.push(`SEMSEARCH=${SEMSEARCH}`)
  }
  const queryString = queries.length ? `?${queries.join('&')}` : ''

  const urlDatePart =
    startDate && endDate ? `/from/${startDate}/to/${endDate}` : ''
  if (updatedSearchType === 'POI' || updatedSearchType === 'AIRPORT') {
    return `/relax/near/${updatedSearchType.toLowerCase()}/${location}${urlDatePart}/rooms/${roomCount}${dealTypeParam}${multiOcc}${queryString}`
  }
  return `/relax/in/${location}${urlDatePart}/rooms/${roomCount}${dealTypeParam}${multiOcc}${queryString}`
}

export const handleSubmitForHotels = (values: HOTEL_FORM_SUBMIT_TYPE) => {
  const {
    roomCount,
    startDate,
    endDate,
    endLocation,
    tripType,
    startLocation,
    adultCount,
    childrenCount,
    childrenAges,
    gaCategory,
    gaAction
  } = values

  const multiOccFields = {
    adultCount,
    childrenCount,
    childrenAges
  }
  analytics.fireEvent({
    category: gaCategory,
    action: gaAction,
    label: 'VALID'
  })

  const isPackageSearch = tripType.includes(FLIGHT) || tripType.includes(CAR)
  const hasFlight = tripType.includes(FLIGHT)

  if (isPackageSearch) {
    const basePackageSearchDetails = {
      roomCount: Number(roomCount),
      startDate,
      endDate,
      endLocationObject: endLocation,
      tripType,
      isPartialStayDateValid: false,
      ...multiOccFields
    }

    const flightSearchDetails = {
      startLocationObject: startLocation,
      ...multiOccFields
    }

    const packageSearchDetails = {
      ...basePackageSearchDetails,
      ...(hasFlight ? flightSearchDetails : {})
    }
    packagesSearch(packageSearchDetails)
  } else {
    const url = formatHotelsURL(values)
    window.open(url)
  }

  persistSearchData(HOTEL_SEARCH_DATA, values)
}

export const handleSubmitForExpressDeals = (values: HOTEL_FORM_SUBMIT_TYPE) => {
  analytics.fireEvent({
    category: values.gaCategory,
    action: ANALYTICS_ACTION_HOTELS_SEARCH,
    label: 'Hotel SOPQ Search'
  })
  const url = formatHotelsURL({
    ...values,
    dealType: PromoDeal.EXPRESS_DEAL
  })
  window.open(url)
}

export const handleSubmitForVIPDeals = (values: HOTEL_FORM_SUBMIT_TYPE) => {
  analytics.fireEvent({
    category: ANALYTICS_CATEGORY_HOME,
    action: 'Feature Box Modal',
    label: 'Hotel Vip Search Button'
  })
  const url = formatHotelsURL({
    ...values,
    dealType: PromoDeal.VIP
  })
  window.open(url)
}

export const handleSubmitForPricebreakers = (
  values: HOTEL_FORM_SUBMIT_TYPE
) => {
  analytics.fireEvent({
    category: ANALYTICS_CATEGORY_HOME,
    action: 'Feature Box Modal',
    label: 'Hotel Collection Search Button'
  })
  const url = formatHotelsURL({
    ...values,
    dealType: PromoDeal.COLLECTIONS
  })
  window.open(url)
}

export const handleSubmitForNHA = (values: HOTEL_FORM_SUBMIT_TYPE) => {
  analytics.fireEvent({
    category: values.gaCategory,
    action: ANALYTICS_ACTION_HOTELS_SEARCH,
    label: 'Hotel NHA Search'
  })
  const url = formatHotelsURL({
    ...values,
    dealType: PromoDeal.NHA
  })
  window.open(url)
}
